<template>
  <v-container fluid class="pa-5">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular indeterminate size="64" :width="5" />
        <span class="my-4">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <!-- ── Approve Dialog with Note ── -->
    <v-dialog v-model="approveDialog" max-width="480" persistent>
      <v-card class="approve-dialog-card">
        <v-card-title class="approve-dialog-title">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          ยืนยันการอนุมัติ
        </v-card-title>
        <v-card-text>
          <div class="approve-info mb-4" v-if="approveTarget">
            <div class="approve-info-row">
              <span class="approve-info-label">ชื่อผู้ขอ</span>
              <span class="approve-info-value">{{ approveTarget.owner?.displayName || '—' }}</span>
            </div>
            <div class="approve-info-row">
              <span class="approve-info-label">จำนวนเงิน</span>
              <span class="approve-info-value font-weight-bold" style="color:#10b981;">
                {{ formatCurrency(approveTarget.amount) }} ฿
              </span>
            </div>
            <div class="approve-info-row">
              <span class="approve-info-label">ธนาคาร</span>
              <span class="approve-info-value">{{ approveTarget.bankName }} — {{ approveTarget.bankAccountNumber }}</span>
            </div>
          </div>
          <v-text-field
            v-model="approveNote"
            label="หมายเหตุ (ไม่บังคับ)"
            placeholder="เช่น โอนแล้ว / รอตรวจสอบยอด"
            outlined
            dense
            hide-details="auto"
            prepend-inner-icon="mdi-note-text-outline"
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="closeApproveDialog">ยกเลิก</v-btn>
          <v-btn color="success" :loading="actionLoading" @click="doApprove">อนุมัติ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <template v-if="!isLoading">
      <v-card class="card-container pa-6" elevation="0">
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="title-text">อนุมัติการถอนเงิน</span>
          <v-btn
            color="success"
            outlined
            small
            :disabled="filteredWithdrawals.length === 0"
            @click="exportExcel"
          >
            <v-icon left small>mdi-microsoft-excel</v-icon>
            Export Excel
          </v-btn>
        </div>

        <v-card-text class="px-0">
          <!-- Filters -->
          <v-row class="mb-2" dense>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="search"
                label="ค้นหา (ชื่อ, เลขบัญชี, ธนาคาร)"
                prepend-inner-icon="mdi-magnify"
                outlined dense hide-details="auto"
                clearable
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                item-text="label"
                item-value="value"
                label="สถานะคำขอ"
                outlined dense hide-details="auto"
                @change="fetchWithdrawals"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-menu v-model="dateFromMenu" :close-on-content-click="false" offset-y min-width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="dateFrom"
                    label="วันที่เริ่มต้น"
                    prepend-inner-icon="mdi-calendar"
                    readonly outlined dense hide-details="auto"
                    clearable
                    v-bind="attrs" v-on="on"
                    @click:clear="dateFrom = null"
                  />
                </template>
                <v-date-picker v-model="dateFrom" no-title @input="dateFromMenu = false" locale="th" />
              </v-menu>
            </v-col>
            <v-col cols="12" md="3">
              <v-menu v-model="dateToMenu" :close-on-content-click="false" offset-y min-width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="dateTo"
                    label="วันที่สิ้นสุด"
                    prepend-inner-icon="mdi-calendar"
                    readonly outlined dense hide-details="auto"
                    clearable
                    v-bind="attrs" v-on="on"
                    @click:clear="dateTo = null"
                  />
                </template>
                <v-date-picker v-model="dateTo" no-title @input="dateToMenu = false" locale="th" />
              </v-menu>
            </v-col>
          </v-row>

          <v-data-table
            :headers="headers"
            :items="filteredWithdrawals"
            :items-per-page="15"
            class="elevation-0"
          >
            <!-- ลำดับ -->
            <template v-slot:[`item.rowNum`]="{ index }">
              {{ index + 1 }}
            </template>

            <!-- รหัสผู้ใช้ -->
            <template v-slot:[`item.refCode`]="{ item }">
              <span class="text-caption font-weight-bold">{{ item.owner?.commisionRefCode?.refCode || '—' }}</span>
            </template>

            <!-- ชื่อผู้ใช้ / ชื่อนามสกุล -->
            <template v-slot:[`item.owner`]="{ item }">
              <div class="d-flex flex-column">
                <span class="font-weight-bold">{{ item.owner?.username || item.owner?.displayName || '—' }}</span>
                <span class="text-caption grey--text">{{ item.owner?.firstName }} {{ item.owner?.lastName }}</span>
                <v-chip x-small label :color="roleColor(item.owner?.role)" class="mt-1 white--text" style="max-width:fit-content">
                  {{ roleText(item.owner?.role) }}
                </v-chip>
              </div>
            </template>

            <!-- วันที่ถอน -->
            <template v-slot:[`item.createdAt`]="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>

            <!-- จำนวนเงิน -->
            <template v-slot:[`item.amount`]="{ item }">
              <span class="font-weight-bold text-primary">
                {{ formatCurrency(item.amount) }}
              </span>
            </template>

            <!-- ธนาคาร + เลขบัญชี + ชื่อบัญชี -->
            <template v-slot:[`item.bank`]="{ item }">
              <div class="d-flex flex-column">
                <span>{{ item.bankName || '-' }}</span>
                <span class="text-caption grey--text">{{ item.bankAccountNumber || '' }}</span>
                <span class="text-caption">{{ item.bankAccountName || '' }}</span>
              </div>
            </template>

            <!-- สถานะ -->
            <template v-slot:[`item.status`]="{ item }">
              <v-chip small :color="getStatusColor(item.status)" text-color="white">
                {{ getStatusText(item.status) }}
              </v-chip>
            </template>

            <!-- วันที่อนุมัติ -->
            <template v-slot:[`item.approvedAt`]="{ item }">
              {{ item.approvedAt ? formatDate(item.approvedAt) : '—' }}
            </template>

            <!-- หมายเหตุ -->
            <template v-slot:[`item.note`]="{ item }">
              <span class="text-caption">{{ item.note || '—' }}</span>
            </template>

            <!-- ผู้อนุมัติ -->
            <template v-slot:[`item.approvedBy`]="{ item }">
              <span class="text-caption">{{ item.approver?.displayName || item.approver?.username || '—' }}</span>
            </template>

            <!-- จัดการ -->
            <template v-slot:[`item.actions`]="{ item }">
              <div class="d-flex">
                <v-btn
                  v-if="item.status === 'pending'"
                  small color="success" class="mr-2"
                  @click="openApproveDialog(item)"
                >
                  อนุมัติ
                </v-btn>
                <v-btn
                  v-if="item.status === 'pending'"
                  small color="error" outlined
                  @click="confirmReject(item)"
                >
                  ปฏิเสธ
                </v-btn>
                <span v-if="item.status !== 'pending'" class="grey--text text--darken-1">-</span>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import * as XLSX from "xlsx";
import swalAlert from "@/services/alert/swalServices";
import WalletService from "@/services/api/wallet";

export default {
  name: "AdminWithdrawals",

  data() {
    return {
      isLoading: false,
      actionLoading: false,
      withdrawals: [],
      search: "",
      statusFilter: "pending",
      dateFrom: null,
      dateTo: null,
      dateFromMenu: false,
      dateToMenu: false,

      // Approve dialog
      approveDialog: false,
      approveTarget: null,
      approveNote: "",

      statusOptions: [
        { label: "ทั้งหมด", value: "all" },
        { label: "รอดำเนินการ", value: "pending" },
        { label: "อนุมัติแล้ว", value: "approved" },
        { label: "ปฏิเสธแล้ว", value: "rejected" },
      ],
      headers: [
        { text: "ลำดับ", value: "rowNum", sortable: false, width: "60px" },
        { text: "รหัสผู้ใช้", value: "refCode", sortable: false },
        { text: "ชื่อผู้ใช้ / ข้อมูล", value: "owner", sortable: false },
        { text: "วันที่ถอน", value: "createdAt" },
        { text: "จำนวนเงิน (฿)", value: "amount" },
        { text: "ธนาคาร / บัญชี", value: "bank", sortable: false },
        { text: "สถานะ", value: "status", sortable: false },
        { text: "วันที่อนุมัติ", value: "approvedAt", sortable: false },
        { text: "ผู้อนุมัติ", value: "approvedBy", sortable: false },
        { text: "หมายเหตุ", value: "note", sortable: false },
        { text: "จัดการ", value: "actions", sortable: false, width: "180px" },
      ],
    };
  },

  computed: {
    filteredWithdrawals() {
      let list = this.withdrawals;

      // keyword search
      const kw = (this.search || "").trim().toLowerCase();
      if (kw) {
        list = list.filter((w) => {
          const name = (w.owner?.displayName || "").toLowerCase();
          const username = (w.owner?.username || "").toLowerCase();
          const email = (w.owner?.email || "").toLowerCase();
          const bank = (w.bankName || "").toLowerCase();
          const bankNo = (w.bankAccountNumber || "").toLowerCase();
          return (
            name.includes(kw) ||
            username.includes(kw) ||
            email.includes(kw) ||
            bank.includes(kw) ||
            bankNo.includes(kw)
          );
        });
      }

      // date range filter
      if (this.dateFrom) {
        const from = new Date(this.dateFrom);
        from.setHours(0, 0, 0, 0);
        list = list.filter((w) => new Date(w.createdAt) >= from);
      }
      if (this.dateTo) {
        const to = new Date(this.dateTo);
        to.setHours(23, 59, 59, 999);
        list = list.filter((w) => new Date(w.createdAt) <= to);
      }

      return list;
    },
  },

  mounted() {
    this.fetchWithdrawals();
  },

  methods: {
    async fetchWithdrawals() {
      this.isLoading = true;
      try {
        const res = await WalletService.listWithdrawals(this.statusFilter);
        this.withdrawals = res.data?.requests || [];
      } catch (e) {
        console.error("fetchWithdrawals:", e);
        swalAlert.Error("โหลดข้อมูลไม่สำเร็จ", e?.response?.data?.message || "");
      } finally {
        this.isLoading = false;
      }
    },

    // ── Approve Dialog ──
    openApproveDialog(item) {
      this.approveTarget = item;
      this.approveNote = "";
      this.approveDialog = true;
    },
    closeApproveDialog() {
      this.approveDialog = false;
      this.approveTarget = null;
      this.approveNote = "";
    },
    async doApprove() {
      if (!this.approveTarget) return;
      this.actionLoading = true;
      try {
        await WalletService.approveWithdrawal(this.approveTarget.id, {
          note: this.approveNote || undefined,
        });
        swalAlert.Success("อนุมัติการถอนเงินสำเร็จ");
        this.closeApproveDialog();
        await this.fetchWithdrawals();
      } catch (e) {
        console.error("Approve withdrawal failed:", e);
        swalAlert.Error("อนุมัติไม่สำเร็จ", e?.response?.data?.message || "ไม่สามารถอนุมัติคำขอได้");
      } finally {
        this.actionLoading = false;
      }
    },

    // ── Reject ──
    async confirmReject(item) {
      const result = await swalAlert.CancelDocumentWithReason(
        "ระบุเหตุผลการปฏิเสธ",
        "เหตุผลที่ปฏิเสธคำขอถอนเงิน"
      );
      if (result.isConfirmed && result.value) {
        await this.reject(item, result.value);
      }
    },
    async reject(item, reason) {
      this.isLoading = true;
      try {
        await WalletService.rejectWithdrawal(item.id, reason);
        swalAlert.Success("ปฏิเสธและคืนเงินสำเร็จ");
        await this.fetchWithdrawals();
      } catch (e) {
        console.error("Reject withdrawal failed:", e);
        swalAlert.Error("ปฏิเสธไม่สำเร็จ", e?.response?.data?.message || "ไม่สามารถปฏิเสธคำขอได้");
      } finally {
        this.isLoading = false;
      }
    },

    // ── Export Excel ──
    exportExcel() {
      const rows = this.filteredWithdrawals.map((w, i) => {
        // ชื่อ-นามสกุล (ถ้าเป็น Seller ใช้ชื่อร้าน ถ้าไม่มีใช้ชื่อจริง)
        const fullName = w.owner?.role === 'seller' 
          ? (w.owner?.shopName || `${w.owner?.firstName || ""} ${w.owner?.lastName || ""}`.trim())
          : `${w.owner?.firstName || w.owner?.natId_firstName || ""} ${w.owner?.lastName || w.owner?.natId_lastName || ""}`.trim();

        return {
          "ลำดับ": i + 1,
          "รหัสผู้ใช้": w.owner?.commisionRefCode?.refCode || "",
          "Username": w.owner?.username || "",
          "ชื่อ-นามสกุล": fullName || w.owner?.displayName || "",
          "วันที่ถอน": w.createdAt ? this.formatDateExcel(w.createdAt) : "",
          "จำนวนเงิน (THB)": Number(w.amount) || 0,
          "ธนาคาร": w.bankName || "",
          "เลขที่บัญชี": w.bankAccountNumber || "",
          "ชื่อบัญชี": w.bankAccountName || "",
          "สถานะ": this.getStatusText(w.status),
          "วันที่อนุมัติ": w.approvedAt ? this.formatDateExcel(w.approvedAt) : "",
          "ผู้อนุมัติ": w.approver?.displayName || w.approver?.username || "",
          "หมายเหตุ": w.note || "",
        };
      });

      const ws = XLSX.utils.json_to_sheet(rows);

      // Set column widths
      ws["!cols"] = [
        { wch: 6 },  // ลำดับ
        { wch: 14 }, // รหัสผู้ใช้
        { wch: 16 }, // Username
        { wch: 22 }, // ชื่อ-นามสกุล
        { wch: 20 }, // วันที่ถอน
        { wch: 16 }, // จำนวนเงิน
        { wch: 10 }, // ธนาคาร
        { wch: 18 }, // เลขที่บัญชี
        { wch: 22 }, // ชื่อบัญชี
        { wch: 12 }, // สถานะ
        { wch: 20 }, // วันที่อนุมัติ
        { wch: 16 }, // ผู้อนุมัติ
        { wch: 28 }, // หมายเหตุ
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Withdrawals");

      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `withdrawals_${dateStr}.xlsx`);
    },

    // ── Helpers ──
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "0.00";
      return new Intl.NumberFormat("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleString("th-TH", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatDateExcel(date) {
      if (!date) return "";
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },

    getStatusColor(status) {
      return { pending: "orange", approved: "green", rejected: "red" }[status] || "grey";
    },

    getStatusText(status) {
      return { pending: "รอดำเนินการ", approved: "อนุมัติแล้ว", rejected: "ปฏิเสธแล้ว" }[status] || status;
    },

    roleText(role) {
      return { user: "ผู้ใช้", seller: "ผู้ขาย", rider: "ไรเดอร์" }[role] || role;
    },

    roleColor(role) {
      return { user: "#3b82f6", seller: "#f59e0b", rider: "#10b981" }[role] || "#6b7280";
    },
  },
};
</script>

<style scoped>
.v-container {
  font-family: "Sarabun", "Noto Sans Thai", sans-serif !important;
}
.title-text {
  font-family: "Sarabun", "Noto Sans Thai", sans-serif !important;
  font-size: 24px;
  font-weight: 700;
  color: #1f2a39;
}
.card-container {
  font-family: "Sarabun", "Noto Sans Thai", sans-serif !important;
  border-radius: 16px !important;
  box-shadow: 0 0 2px 0 #1018280d !important;
}

/* Approve Dialog */
.approve-dialog-card {
  border-radius: 16px !important;
  font-family: "Sarabun", "Noto Sans Thai", sans-serif !important;
}
.approve-dialog-title {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: #1f2a39 !important;
  padding: 20px 24px 8px !important;
}
.approve-info {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px 16px;
}
.approve-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}
.approve-info-label {
  font-size: 13px;
  color: #6b7280;
}
.approve-info-value {
  font-size: 14px;
  color: #111827;
  font-weight: 500;
}

::v-deep .v-data-table {
  font-family: "Sarabun", "Noto Sans Thai", sans-serif !important;
}
::v-deep .v-data-table th {
  font-family: "Sarabun", "Noto Sans Thai", sans-serif !important;
  font-weight: 600 !important;
  background: #f8fafc !important;
}
::v-deep .v-data-table td {
  font-family: "Sarabun", "Noto Sans Thai", sans-serif !important;
}
::v-deep .v-text-field,
::v-deep .v-select {
  font-family: "Sarabun", "Noto Sans Thai", sans-serif !important;
}
</style>
